import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import ThemeToggle from "./ThemeToggle";
import DesignModeToggle from "./terminal/DesignModeToggle";
import { BUILD_VERSION, BUILD_SHA, BUILD_TIME_ISO } from "../lib/buildInfo";

/**
 * Site-wide footer rendered below every page.
 *
 * Iter 275g — Now hosts the utilities that used to clutter the top
 * nav: language switcher, light/dark theme toggle, Classic⇌Terminal
 * design-mode toggle, and the ★ GOLD entry point (premium users +
 * admins only).  The top rail keeps only the primary nav so the
 * search bar and Place Parlay CTA breathe on 1440-1920px screens.
 */
export const Footer = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const year = new Date().getFullYear();
  const showGold = !!(user?.is_gold || user?.is_admin);

  return (
    <footer
      className="border-t border-border bg-card/40 mt-16"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/ndakubet-logo.jpg"
              alt="NDAKUBET"
              className="h-9 w-9 rounded-xl object-cover"
            />
            <div>
              <div className="font-display font-bold tracking-tight text-base">
                NDAKUBET
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground -mt-0.5">
                AI Football Intel
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
            {t("footer.tagline")}
          </p>
          <p className="text-xs text-muted-foreground/80 max-w-md leading-relaxed">
            {t("footer.responsible")}
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            {t("nav.dailySlips") /* reusing "Daily Slips" header — generic */}
          </div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" className="hover:text-primary transition-colors" data-testid="footer-link-predictions">
                {t("footer.links.predictions")}
              </Link>
            </li>
            <li>
              <Link to="/tournaments" className="hover:text-primary transition-colors" data-testid="footer-link-tournaments">
                {t("footer.links.tournaments")}
              </Link>
            </li>
            <li>
              <Link to="/history" className="hover:text-primary transition-colors" data-testid="footer-link-history">
                {t("footer.links.history")}
              </Link>
            </li>
            <li>
              <Link to="/accuracy" className="hover:text-primary transition-colors" data-testid="footer-link-accuracy">
                {t("footer.links.accuracy")}
              </Link>
            </li>
            <li>
              <Link to="/account" className="hover:text-primary transition-colors" data-testid="footer-link-account">
                {t("footer.links.account")}
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-primary transition-colors" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            {t("language.label")}
          </div>
          <LanguageSwitcher />

          {/* Iter 275g — Preferences moved here from the top nav. */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
              Preferences
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <div className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-background/40 px-3 py-1.5">
                <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  Theme
                </span>
                <ThemeToggle />
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-border/60 bg-background/40 px-3 py-1.5">
                <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  Design
                </span>
                <DesignModeToggle />
              </div>
              {showGold && (
                <Link
                  to="/gold"
                  data-testid="footer-gold-link"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-yellow-400/5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-amber-300 hover:from-amber-500/20 hover:to-yellow-400/10 hover:text-amber-200 transition-all"
                >
                  ★ GOLD
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>© {year} NDAKUBET. {t("footer.rights")}</span>
          <div className="flex items-center gap-3">
            <span>{t("common.responsibleGambling")}</span>
            <span
              className="font-mono text-[10px] tracking-tight text-muted-foreground/60 hover:text-muted-foreground transition-colors cursor-help"
              data-testid="footer-build-version"
              title={`Build ${BUILD_VERSION}${BUILD_SHA ? ` · ${BUILD_SHA}` : ""}\nDeployed ${BUILD_TIME_ISO}\n\nTip: the version is the UTC time this bundle was compiled.\nMatching git SHAs between preview and prod = same code.`}
            >
              {BUILD_VERSION}{BUILD_SHA ? ` · ${BUILD_SHA}` : ""}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
